import React from 'react';
import { Search, Bell, Settings, ChevronDown, Building2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import storilyIcon from '@/assets/storily-icon.png';
import storilyIconDark from '@/assets/storily-icon-dark.png';

interface MockupHeaderProps {
  animationDelay?: number;
  customBrand?: {
    name: string;
    icon?: React.ReactNode;
  };
}

const MockupHeader: React.FC<MockupHeaderProps> = ({ animationDelay = 0, customBrand }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div 
      className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-2.5 border-b"
      style={{
        borderColor: isDark ? '#333333' : '#E5E5E5',
        backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
        animation: `slideInFromTop 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Logo */}
      <div 
        className="flex items-center gap-2"
        style={{ animation: `slideInFromLeft 0.5s ease-out ${animationDelay + 100}ms both` }}
      >
        {customBrand ? (
          <>
            {customBrand.icon || (
              <div 
                className="w-5 h-5 rounded flex items-center justify-center"
                style={{ backgroundColor: isDark ? '#333' : '#F0F0F0' }}
              >
                <Building2 className="w-3 h-3" style={{ color: isDark ? '#FFF' : '#111' }} />
              </div>
            )}
            <span 
              className="font-semibold text-xs sm:text-sm"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              {customBrand.name}
            </span>
          </>
        ) : (
          <>
            <img 
              src={isDark ? storilyIconDark : storilyIcon}
              alt="Storily"
              width={20}
              height={20}
              className="w-5 h-5 object-contain"
            />
            <span 
              className="font-semibold text-xs sm:text-sm"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Storily
            </span>
          </>
        )}
      </div>

      {/* Search bar - hidden on mobile to prevent overflow */}
      <div 
        className="hidden sm:flex flex-1 max-w-xs mx-4"
        style={{ animation: `fadeIn 0.5s ease-out ${animationDelay + 200}ms both` }}
      >
        <div 
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg w-full"
          style={{ 
            backgroundColor: isDark ? '#2A2A2A' : '#F5F5F5',
            border: `1px solid ${isDark ? '#3A3A3A' : '#E0E0E0'}`
          }}
        >
          <Search className="w-3 h-3 flex-shrink-0" style={{ color: isDark ? '#666666' : '#999999' }} />
          <div 
            className="text-xs overflow-hidden whitespace-nowrap"
            style={{ 
              color: isDark ? '#666666' : '#999999',
              animation: 'typing 2s steps(20) infinite',
              width: '80px'
            }}
          >
            Search leads...
          </div>
        </div>
      </div>

      {/* Right actions */}
      <div 
        className="flex items-center gap-3"
        style={{ animation: `slideInFromRight 0.5s ease-out ${animationDelay + 150}ms both` }}
      >
        <button 
          className="relative p-1.5 rounded-md hover:opacity-80 transition-opacity"
          style={{ backgroundColor: isDark ? '#2A2A2A' : '#F5F5F5' }}
          aria-label="View notifications"
        >
          <Bell className="w-3.5 h-3.5" style={{ color: isDark ? '#999999' : '#666666' }} />
          <div 
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full"
            style={{ 
              backgroundColor: '#EF4444',
              animation: 'pulse 2s infinite'
            }}
          />
        </button>
        <button 
          className="hidden sm:block p-1.5 rounded-md hover:opacity-80 transition-opacity"
          style={{ backgroundColor: isDark ? '#2A2A2A' : '#F5F5F5' }}
          aria-label="Open settings"
        >
          <Settings className="w-3.5 h-3.5" style={{ color: isDark ? '#999999' : '#666666' }} />
        </button>
        <div 
          className="flex items-center gap-1.5 pl-2 border-l"
          style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
        >
          <div 
            className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-medium"
            style={{ 
              backgroundColor: isDark ? '#3B82F6' : '#3B82F6',
              color: '#FFFFFF'
            }}
          >
            JD
          </div>
          <ChevronDown className="w-3 h-3" style={{ color: isDark ? '#666666' : '#999999' }} />
        </div>
      </div>
    </div>
  );
};

export default MockupHeader;
